/**
 * @fileoverview Store authenticated user data in Convex
 *
 * This component automatically stores authenticated user data from Clerk
 * into the Convex database when a user signs in. This ensures the user
 * record exists for all Convex queries that require user lookup.
 *
 * @module components/UserStoreInitializer
 */

import { useEffect } from 'react'
import { useAuth } from '@clerk/clerk-react'
import { useMutation } from 'convex/react'
import { api } from 'convex/_generated/api'

/**
 * UserStoreInitializer Component
 *
 * Automatically calls users.store mutation when user is signed in.
 * This creates/updates the user record in Convex database with data from Clerk.
 *
 * Usage: Add this component once at the root of your app
 */
export function UserStoreInitializer() {
  const { isSignedIn } = useAuth()
  const storeUser = useMutation(api.users.store)

  useEffect(() => {
    // Only store user data if signed in
    if (!isSignedIn) {
      return
    }

    // Check if we've already stored this session to avoid redundant calls
    const hasStoredThisSession = sessionStorage.getItem('user_stored_in_convex')
    if (hasStoredThisSession) {
      return
    }

    // Store user in Convex database
    const storeUserData = async () => {
      try {
        await storeUser({})
        sessionStorage.setItem('user_stored_in_convex', 'true')
        console.log('[UserStore] User data stored in Convex database')
      } catch (error) {
        console.error('[UserStore] Failed to store user data:', error)
      }
    }

    storeUserData()
  }, [isSignedIn, storeUser])

  // This component doesn't render anything
  return null
}
